import { useCallback, useEffect, useMemo, useState } from 'react';
import constants from '../../config/constants';
import { CookieStorage } from '../CookieStorage';
import { WebStorage } from '../WebStorage';

const { AVAILABILITY_PICKUPINSTORE, AVAILABILITY_STOCKINSTORE, AVAILABILITY_HOMEDELIVERY } = constants;
const SELECTED_HD = 'SELECTED_HD';
const SELECTED_CC = 'SELECTED_CC';

export const setToWebStorage = (key, regionObj) => {
  WebStorage.setItem({
    name: key,
    value: JSON.stringify(regionObj),
    shouldPersist: true,
  });
};

export const getFromWebStorage = (key) => {
  try {
    return JSON.parse(
      WebStorage.getItem({
        name: key,
        fromPersistence: true,
      })
    );
  } catch (err) {
    return null;
  }
};

export const getFromCookie = (key) => {
  const cookieVal = CookieStorage.getCookie(key);
  const parsedCookieVal = CookieStorage.parseCookieValue(key, cookieVal);
  return parsedCookieVal;
};

const useDeliveryDataPersistence = ({ store, selectedZone, regionCode, currentVariantId }) => {
  const [pickupInStoreData, setPickupInStoreDataState] = useState([]);
  const [homeDeliveryData, setHomeDeliveryDataState] = useState([]);
  const [storesList, setStoresListState] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [textGenerated, setTextGenerated] = useState({
    StockInStore: {},
    HomeDelivery: {},
    PickupInStore: {},
  });
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState({
    PickupInStore: {},
    HomeDelivery: {},
    StockInStore: {},
  });

  const [deliveryErrors, setDeliveryErrors] = useState({
    PickupInStore: { isError: false, code: null },
    HomeDelivery: { isError: false, code: null },
    StockInStore: { isError: false, code: null },
  });

  useEffect(() => {
    setSelectedDeliveryOption({
      PickupInStore: {},
      HomeDelivery: {},
      StockInStore: {},
    });
  }, [currentVariantId]);

  const setPickupInStoreData = useCallback((data) => {
    setPickupInStoreDataState(data);
  }, []);

  const setHomeDeliveryData = useCallback((data) => {
    setHomeDeliveryDataState(data);
  }, []);

  const setStoresList = useCallback((data) => {
    setStoresListState(data);
  }, []);

  const saveSelectedDeliveryOption = useCallback(
    (option) => {
      const optionToSave = { ...option, currentVariantId };
      const { isError, deliveryOptionType } = optionToSave;
      const key = deliveryOptionType === 'HomeDelivery' ? SELECTED_HD : SELECTED_CC;

      if (deliveryOptionType === 'HomeDelivery' && !isError && selectedZone?.parentStateName) {
        optionToSave.parentStateName = selectedZone.parentStateName;
      }

      CookieStorage.setCookie({
        name: key,
        value: optionToSave,
        regionCode,
        store,
      });
      setToWebStorage(key, optionToSave);
      setSelectedDeliveryOption((prev) => ({
        ...prev,
        [deliveryOptionType]: optionToSave,
      }));
    },
    [currentVariantId, regionCode, selectedZone, store]
  );

  const clearDeliveryData = useCallback(() => {
    setPickupInStoreDataState([]);
    setHomeDeliveryDataState([]);
    setStoresListState([]);
  }, []);

  const setDeliveryError = useCallback((deliveryType, hasError, errorCode = null) => {
    const typeMap = {
      [AVAILABILITY_PICKUPINSTORE]: 'PickupInStore',
      [AVAILABILITY_HOMEDELIVERY]: 'HomeDelivery',
      [AVAILABILITY_STOCKINSTORE]: 'StockInStore',
    };

    const errorKey = typeMap[deliveryType];
    if (!errorKey) return;

    setDeliveryErrors((prev) => ({
      ...prev,
      [errorKey]: {
        isError: hasError,
        code: hasError ? errorCode : null,
      },
    }));
  }, []);

  const clearDeliveryErrors = useCallback(() => {
    setDeliveryErrors({
      PickupInStore: { isError: false, code: null },
      HomeDelivery: { isError: false, code: null },
      StockInStore: { isError: false, code: null },
    });
  }, []);

  const deliveryData = useMemo(
    () => ({
      pickupInStoreData,
      homeDeliveryData,
      storesList,
    }),
    [pickupInStoreData, homeDeliveryData, storesList]
  );

  const deliveryActions = useMemo(
    () => ({
      setPickupInStoreData,
      setHomeDeliveryData,
      setStoresList,
      clearDeliveryData,
      setTextGenerated,
      setDeliveryError,
      clearDeliveryErrors,
      saveSelectedDeliveryOption,
    }),
    [
      setPickupInStoreData,
      setHomeDeliveryData,
      setStoresList,
      clearDeliveryData,
      setTextGenerated,
      setDeliveryError,
      clearDeliveryErrors,
      saveSelectedDeliveryOption,
    ]
  );

  return {
    deliveryData,
    deliveryActions,
    selectedType,
    setSelectedType,
    textGenerated,
    deliveryErrors,
    selectedDeliveryOption,
    setSelectedDeliveryOption: saveSelectedDeliveryOption,
  };
};

export default useDeliveryDataPersistence;
